import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import ServicesService from "../../services/ServicesService";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  TextField,
  Avatar,
  ListItemAvatar,
} from "@mui/material";

function getProductImageUrl(product) {
  if (!product) return null;
  if (product.image_url) return product.image_url;
  const images = product.images;
  if (!Array.isArray(images) || images.length === 0) return null;
  const first = images[0];
  if (!first) return null;
  if (typeof first === "object" && first.url) return first.url;
  if (typeof first === "string") {
    try {
      const parsed = JSON.parse(first);
      return parsed?.url || null;
    } catch {
      return null;
    }
  }
  return null;
}

function ProductListAvatar({ product }) {
  const imageUrl = getProductImageUrl(product);
  const label = product?.name || product?.title || product?.product_name || "?";
  return (
    <ListItemAvatar sx={{ minWidth: 48 }}>
      <Avatar
        src={imageUrl || undefined}
        variant="rounded"
        sx={{
          width: 40,
          height: 40,
          bgcolor: imageUrl ? "transparent" : "grey.200",
          color: "text.secondary",
          fontSize: 14,
        }}
      >
        {!imageUrl && label.charAt(0).toUpperCase()}
      </Avatar>
    </ListItemAvatar>
  );
}

export const CatalogMessageNode = ({ data, id, selected = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productDialogSearch, setProductDialogSearch] = useState("");
  const menuRef = useRef(null);

  // Edit popup state
  const [editText, setEditText] = useState(data.text || "");
  const [textError, setTextError] = useState("");
  const [text, setText] = useState(data.text || "");
  const [catalogId, setCatalogId] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(data.products || []);
  console.log('id', catalogId);

  const isHandleConnected = useCallback(
    (handleId) => {
      if (!data.edges || !Array.isArray(data.edges)) {
        return false;
      }
      return data.edges.some((edge) => edge.sourceHandle === handleId);
    },
    [data.edges]
  );

  const mainHandleId = `${id}-main`;
  const mainHandleConnected = isHandleConnected(mainHandleId);
  

  useEffect(() => {
    setText(data.text || "");
  }, [data.text]);

  // useEffect(() => {
  //   if (showEditPopup) {
  //     console.log("=== Catalog Message Edit Popup Opened ===", { id });
  //   }
  // }, [showEditPopup, id]);

  async function fetchCatalogId() {
    ServicesService.getCatalog().then((res) => {
      ServicesService.getCatalogProducts(res).then((resp) => {
        setCatalogProducts(Array.isArray(resp) ? resp : []);
      });
      setCatalogId(res);
  });
  }

  console.log('catalogPro', catalogProducts);
  

  useEffect(() => {
    fetchCatalogId();
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const validateText = (value) => {
    if (value.length > 50) {
      setTextError("Message cannot exceed 50 characters");
      return false;
    }
    setTextError("");
    return true;
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setEditText(value);
    validateText(value);
  };

  const handleSave = () => {
    const isTextValid = validateText(editText);
    if (isTextValid) {
      if (data.onChangeText) data.onChangeText(id, editText);
      setText(editText);
      setShowEditPopup(false);
      setShowMenu(false);
    }
  };

  const handleCancel = () => {
    setEditText(text || "");
    setTextError("");
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const handleEdit = () => {
    setEditText(text || "");
    setShowEditPopup(true);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
    setShowMenu(false);
  };

  const filteredCatalogProducts = useMemo(() => {
    const q = productDialogSearch.trim().toLowerCase();
    if (!q) return catalogProducts;
    return catalogProducts.filter((p) => {
      const pid = String(p.catalog_product_id || p.product_id || p.id || p.retailer_id || "");
      const name = String(p.name || p.title || p.product_name || "");
      const sku = String(p.sku || p.product_sku || "");
      const retailer = String(p.retailer_id || "");
      return (
        pid.toLowerCase().includes(q)
        || name.toLowerCase().includes(q)
        || sku.toLowerCase().includes(q)
        || retailer.toLowerCase().includes(q)
      );
    });
  }, [catalogProducts, productDialogSearch]);

  const handleOpenProductDialog = () => {
    setProductDialogSearch("");
    setProductDialogOpen(true);
  };
  const handleCloseProductDialog = () => {
    setProductDialogSearch("");
    setProductDialogOpen(false);
  };
  const handleToggleProduct = (product) => {
    const exists = selectedProducts.some((p) => p.catalog_product_id === product.catalog_product_id);
    let updated;
    if (exists) {
      updated = selectedProducts.filter((p) => p.catalog_product_id !== product.catalog_product_id);
    } else {
      if (selectedProducts.length >= 30) return;
      updated = [...selectedProducts, { ...product, node_id: id }];
    }
    setSelectedProducts(updated);
  };
  const handleSaveProducts = () => {
    if (data.onChangeProduct) data.onChangeProduct(id, selectedProducts);
    setProductDialogOpen(false);
  };

  const menuStyle = {
    position: "absolute",
    top: 30,
    right: 8,
    background: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 2,
    minWidth: "100px",
  };

  const menuItemStyle = {
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    borderBottom: "1px solid #eee",
    transition: "background-color 0.2s",
  };

  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const popupStyle = {
    background: "white",
    borderRadius: "8px",
    padding: "0",
    width: "600px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const errorStyle = {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
  };

  const charCountStyle = {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "right",
    marginTop: "4px",
    marginBottom: "4px",
  };

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: "6px",
          padding: "8px",
          width: "160px",
          minHeight: "60px",
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            zIndex: 1,
            userSelect: "none",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            height: "11px",
          }}
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋮
        </div>

        {showMenu && (
          <div ref={menuRef} style={menuStyle}>
            <div
              style={menuItemStyle}
              onClick={handleEdit}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              ✏️ Edit
            </div>
            <div
              style={{ ...menuItemStyle, borderBottom: "none" }}
              onClick={handleDelete}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          style={{ background: selected ? "#1976d2" : "#6c757d" }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id={mainHandleId}
          style={{
            background: mainHandleConnected
              ? "#28a745"
              : selected
              ? "#1976d2"
              : "#6c757d",
            width: "8px",
            height: "8px",
            right: "-4px",
            top: "50%",
            transform: "translateY(-50%)",
            border: "2px solid white",
            opacity: mainHandleConnected ? 0.7 : 1,
            cursor: mainHandleConnected ? "not-allowed" : "crosshair",
            pointerEvents: mainHandleConnected ? "none" : "auto",
          }}
        />
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontWeight: "bold",
              color: selected ? "#1976d2" : "#6c757d",
              marginBottom: "8px",
              fontSize: "11px",
            }}
          >
            Catalog Message
          </div>

          {text && text.trim() ? (
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  minHeight: "40px",
                  textAlign: "left",
                }}
              >
                {text}
              </div>
            </div>
          ) : (
            <div
              style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  fontStyle: "italic",
                  textAlign: "left",
                }}
              >
                Click Edit to add catalog message
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Popup Modal */}
      {showEditPopup &&
        createPortal(
          <>
            <div style={popupOverlayStyle} onClick={handleCancel}>
              <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
                {/* Header with title and close button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "24px 24px 16px 24px",
                    borderBottom: "1px solid #e9ecef",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    zIndex: 1,
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: "#333",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    Catalog Message
                  </h3>
                  <div
                    style={{ display: "flex", gap: "8px", alignItems: "center" }}
                  >
                    <button
                      onClick={handleSave}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #28a745",
                        borderRadius: "4px",
                        backgroundColor: "#28a745",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      style={{
                        padding: "6px 8px",
                        border: "1px solid #6c757d",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        color: "#6c757d",
                        cursor: "pointer",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                      }}
                      title="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Content Area */}
                <div style={{ padding: "0 24px 24px 24px" }}>
                  {/* Catalog Message Field */}
                  <div style={{ marginTop: "8px", marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "14px",
                      }}
                    >
                      Catalog Message
                    </label>
                    <textarea
                      value={editText}
                      onChange={handleTextChange}
                      placeholder="Enter your catalog message..."
                      rows={6}
                      style={{
                        ...inputStyle,
                        borderColor: textError ? "#dc3545" : "#ddd",
                        resize: "vertical",
                        minHeight: "120px",
                      }}
                    />
                    <div style={charCountStyle}>{editText.length}/1024</div>
                    {textError && <div style={errorStyle}>{textError}</div>}
                  </div>
                  {/* Choose Products Button */}
                  <Button variant="outlined" onClick={handleOpenProductDialog} sx={{ mb: 2 }}>
                    Choose Products ({selectedProducts.length}/30)
                  </Button>
                  {/* Show selected products */}
                  {selectedProducts.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Selected Products:</Typography>
                      <List dense>
                        {selectedProducts.map((prod) => (
                          <ListItem key={prod.catalog_product_id}>
                            <ProductListAvatar product={prod} />
                            <ListItemText primary={prod.name} secondary={prod.catalog_product_id} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </div>
                {/* Product Selection Dialog is now rendered outside the overlay for correct z-index */}
              </div>
            </div>
            {/* Product Selection Dialog rendered at root level for stacking */}
            <Dialog open={productDialogOpen} onClose={handleCloseProductDialog} maxWidth="sm" fullWidth sx={{ zIndex: 13010 }}>
              <DialogTitle>Select up to 30 Products</DialogTitle>
              <DialogContent dividers>
                <TextField
                  label="Search products"
                  placeholder="Search by name, ID, SKU, or retailer"
                  value={productDialogSearch}
                  onChange={(e) => setProductDialogSearch(e.target.value)}
                  fullWidth
                  size="small"
                  autoFocus
                  sx={{ mb: 2, mt: 1 }}
                  helperText={`${filteredCatalogProducts.length} shown · ${selectedProducts.length}/30 selected`}
                />
                <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                  <List dense disablePadding>
                    {filteredCatalogProducts.map((product) => (
                      <ListItem
                        key={product.catalog_product_id}
                        button
                        onClick={() => handleToggleProduct(product)}
                      >
                        <Checkbox
                          edge="start"
                          checked={selectedProducts.some((p) => p.catalog_product_id === product.catalog_product_id)}
                          tabIndex={-1}
                          disableRipple
                        />
                        <ProductListAvatar product={product} />
                        <ListItemText
                          primary={product.name || product.title || product.product_name}
                          secondary={`ID: ${product.catalog_product_id} | Retailer: ${product.retailer_id}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {filteredCatalogProducts.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      {catalogProducts.length === 0
                        ? "No products in catalog."
                        : "No products match your search."}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseProductDialog}>Cancel</Button>
                <Button onClick={handleSaveProducts} variant="contained" disabled={selectedProducts.length === 0}>
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </>,
          document.body
        )}
    </>
  );
};
